import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {Subscription} from "rxjs";
import {BmCustomerAccountObjectBaseComponent} from "../bm-customer-account-object-base.component";
import {
    BmCustomerBillingAccountChangeWizardComponent
} from "../../../bm-customer-billing-account-change-wizard/bm-customer-billing-account-change-wizard.component";
import {WidgetData} from "../../../../../../../core/shared/widget/widget-data";
import {WidgetFactory} from "../../../../../../../core/shared/widget/service/widget.factory";
import {WidgetHelperButton} from "../../../../../../../core/shared/basic/widget-helper-button";
import {FilterCriteria} from "../../../../../../../core/shared/filter/api/filter.criteria";
import {Sorting} from "../../../../../../../core/shared/misc/sorting";
import {CustomerBillingAccountService} from "../../../../../service/api/customer-billing-account.service";
import {MvsObjectService} from "../../../../../../../core/shared/service/object/mvs-object.service";
import {DtoList} from "../../../../../../../core/shared/dto/dto.list";
import {DtoImportObjectContext} from "../../../../../../../core/shared/dto/dto.import.object.context";
import {DtoImportObjectContextEntry} from "../../../../../../../core/shared/dto/dto.import.object.context.entry";
import {ContractPaymentAccountDto} from "../../../../../model/dto/entity/contract-payment-account.dto";
import {MvsObjectNavigationService} from "../../../../../../../core/shared/navigation/mvs-object-navigation.service";
import {MvsObjectNavigationEntry} from "../../../../../../../core/shared/navigation/mvs-object-navigation-entry";
import {
    MvsObjectNavigationActionEnum
} from "../../../../../../../core/shared/navigation/mvs-object-navigation-action-enum";
// TM-1000001932: cross-feature read of the BD-owned anticipated-storno pre-booking tracker.
import {BdAnticipatedStornoBookingService} from "../../../../../../bd/service/api/bd-anticipated-storno-booking.service";
import {DunningAnticipatedStornoBookingDto} from "../../../../../../bd/model/dto/entity/dunning-anticipated-storno-booking.dto";
// TM-1000002026: BM_PAYMENT_FREQUENCY_LABELS is read by the frequency helper and the row model now
import {
    BM_PAYMENT_DAY_OF_MONTH_SHORT_LABELS,
    isBmDirectDebitPaymentMethod
} from "../../../../../logic/payment-enum-labels/bm-payment-enum-labels";
// TM-1000002026: the header lists the frequencies of every not-ended assignment, upcoming ones with „ab {Datum}"
import {
    bmCalendarDayNumberToDe,
    bmPaymentFrequencyOccurrenceLabel,
    collectBmCurrentAndUpcomingPaymentFrequencies
} from "../../../../../logic/active-payment-frequencies/bm-active-payment-frequencies";
// TM-1000002026: the header tag shape lives in its own file (one interface per file)
import type {
    BmAccountHeaderFrequencyTag
} from "../../../../../logic/active-payment-frequencies/bm-account-header-frequency-tag";
// TM-1000002026: one precomputed display row per assignment slice of the Verträge table
import type {BmContractLinkRow} from "../../../../../logic/contract-links/bm-contract-link-row";
import {bmBuildContractLinkRows} from "../../../../../logic/contract-links/bm-contract-link-rows";
import type {
    BmBillingAccountDetailSection
} from "../../../../protected-components/bm-customer-billing-accounts-overview/bm-billing-account-section-selection.interface";
import {BM_CW_CASE_OBJECT_TYPE} from "../../../../../logic/open-cw-case-lines/bm-open-cw-case-lines-ql";
import type {BmOpenCwCaseLine} from "../../../../../logic/open-cw-case-lines/bm-open-cw-case-line";

/** Object type of the section deep link entry of the import context (E-4). */
const BM_CUSTOMER_BILLING_ACCOUNT_OBJECT_TYPE_ALIAS = 'bm.CustomerBillingAccount';

/**
 * Tooltip of a header frequency tag of an assignment that is active today. „Zahlungsfrequenz", not
 * „Zahlweise": in this view „Zahlweise" is the payment METHOD (SEPA-Lastschrift / Selbstüberweisung).
 *
 * @ticket TM-1000002026
 */
const TOOLTIP_FREQUENCY_ACTIVE = 'Zahlungsfrequenz aktiver Verträge dieses Kontos';

/**
 * Tooltip of a header frequency tag that occurs on not-yet-started assignments only, completed by the
 * earliest start (dd.MM.yyyy).
 *
 * @ticket TM-1000002026
 */
const TOOLTIP_FREQUENCY_UPCOMING_PREFIX =
    'Künftige Zahlungsfrequenz – die erste Zuordnung mit dieser Frequenz beginnt am ';

/** Toolbar action id of the balance refresh. */
const ACTION_REFRESH_BALANCE = 'refresh-balance';

/** Toolbar action id opening the „Abrechnungskonto ändern“ wizard (account mode). */
const ACTION_OPEN_BILLING_ACCOUNT_CHANGE_WIZARD = 'open-billing-account-change-wizard';

/**
 * Label, icon and tooltip of every header action, held as constants rather than inline literals so
 * the German wording of an action lives in exactly one place.
 */
const LABEL_REFRESH_BALANCE = 'Saldo aktualisieren';
const ICON_REFRESH_BALANCE = 'fa-regular fa-arrows-rotate';
const TOOLTIP_REFRESH_BALANCE = 'Saldo dieses Kontos neu berechnen';

const LABEL_BILLING_ACCOUNT_CHANGE = 'Abrechnungskonto ändern';
const ICON_BILLING_ACCOUNT_CHANGE = 'fa-regular fa-credit-card';
const TOOLTIP_BILLING_ACCOUNT_CHANGE = 'Zahlweise, Fälligkeitstag oder Zahlungsfrequenz dieses Kontos ändern';

/**
 * The completeness notice above the timeline of the tab „Verlauf" - the wording signed in
 * projects/bm-account-history (capability matrix §5 K-5). Do not reword it.
 */
const HISTORY_NOTICE = 'Gezeigt werden die Änderungen, die die Anwendung aufgezeichnet hat. Nicht enthalten sind '
    + 'Datenübernahmen, Migrationen und technische Korrekturen, Änderungen vor Beginn der Aufzeichnung sowie '
    + 'technische Felder. Bankverbindungswechsel vor Einführung der zeitabhängigen Bankverbindung sind nicht '
    + 'enthalten. Als Bearbeiter steht der Anmeldename, bei Schnittstellen und Jobs ein technischer Nutzer.';

/** Empty state of the tab „Verlauf". */
const HISTORY_EMPTY_MESSAGE = 'Für dieses Abrechnungskonto ist noch keine Änderung aufgezeichnet.';

/**
 * German names of the object types in the history scope of the account. They win over the platform entity
 * labels on the timeline's chips („Kunden Konto/ Zahlungsmandat“, „Bankverbindung“ are too close to each other
 * here) and name a type the user may not read („… ausgeblendet, da die Leseberechtigung fehlt: Bankkonto.“).
 * Local on purpose: the platform labels stay as they are everywhere else.
 */
const HISTORY_OBJECT_TYPE_LABELS: { [objectAlias: string]: string } = {
    'bm.CustomerBillingAccount': 'Abrechnungskonto',
    'bm.CustomerBillingAccountBank': 'Bankverbindung (zeitabhängig)',
    'cr.CustomerBankAccount': 'Bankkonto'
};

/**
 * The open-ended dates of the history scope: the end of a bank slice reads „unbefristet“ at 9999-12-31 (or without a
 * value), exactly as the tab „Bankverbindungen & IBANs“ prints it (bmBankSliceEndDateForDisplay). A start date -
 * 01.01.1900 included - is a plain date there and here.
 */
const HISTORY_OPEN_END_ATTRIBUTES: { [objectAlias: string]: string[] } = {
    'bm.CustomerBillingAccountBank': ['endDate']
};

@Component({
    selector: 'bm-customer-account-object-component-full',
    templateUrl: 'bm-customer-account-object-full.component.html',
    standalone: false
})
export class BmCustomerAccountObjectFullComponent extends BmCustomerAccountObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    // TM-1000002026: expand/collapse state per ASSIGNMENT (bm.ContractPaymentAccount id), not per
    // contract - two slices of one contract used to open and close together
    expandedContractRows: { [assignmentId: number]: boolean } = {};

    // Cached widget data of the expanded rows, per assignment id (the widgets still read the contract)
    contractPriceWidgets: { [assignmentId: number]: WidgetData } = {};
    contractPaymentPlannedWidgets: { [assignmentId: number]: WidgetData } = {};

    /**
     * The rows the Verträge table renders - one per visible assignment slice, in the order of
     * {@link visibleContractPaymentAccounts}, with every cell text and tag style derived once
     * (bm-contract-link-rows). A STABLE array reference like the visible list it is built from, rebuilt
     * only by {@link recomputeVisibleContractPaymentAccounts}.
     *
     * @ticket TM-1000002026
     */
    contractLinkRows: BmContractLinkRow<ContractPaymentAccountDto>[] = [];

    /**
     * Row identity of the Verträge table for PrimeNG's [rowTrackBy]: the assignment id (`row.key`), the
     * index only for a row without one. {@link contractLinkRows} holds NEW row objects after every
     * recompute (load, switch); with the default identity (the row object itself) every row - and with it
     * the sub-widgets of an expanded row - would be destroyed and built again on each toggle of the switch.
     *
     * @ticket TM-1000002026
     */
    readonly contractLinkRowTrackBy = (index: number, row: BmContractLinkRow | null | undefined): number =>
        row?.key ?? index;

    showContractPriceDetail: boolean = false;
    contractPriceDetailWidget: WidgetData | null = null;

    /**
     * The payment frequencies of every assignment of this account that has not ENDED - active today OR
     * starting later (rule: collectBmCurrentAndUpcomingPaymentFrequencies), in enum order — rendered as
     * tags in the header. A frequency that occurs on not-yet-started assignments only reads „… ab
     * {earliest start}" (TM-1000002026: an account with a frequency change written ahead - e.g.
     * 60010001, halbjährlich until 30.11.2026, monthly from 01.11./01.12.2026 - showed only „Halbjährlich").
     *
     * A field and not a getter, for the same reason as visibleContractPaymentAccounts: the header binds
     * it inside an @for, and a getter would hand Angular a new array on every change-detection tick.
     * Rebuilt only from the load hook {@link onCustomerBillingAccountLoaded}.
     */
    contractFrequencyTags: BmAccountHeaderFrequencyTag[] = [];

    // ------------------------------------------------------------------
    // Account header action bar + the „Abrechnungskonto ändern“ wizard launcher.
    // ------------------------------------------------------------------

    /**
     * The actions of the account header, declared as framework toolbar buttons and dispatched by
     * their action id in {@link #handleAccountToolbarClick}.
     *
     * This view has no widget in its header, so the array cannot be handed to an mvs-widget
     * toolbar — it is rendered as the header's own action bar. Declaring the actions here anyway
     * keeps ONE place that owns label, icon, tooltip and visibility per action, and gives the view a
     * single click seam instead of one ad-hoc (onClick) per raw button. "Saldo aktualisieren" was
     * migrated into it rather than left standing next to it.
     */
    accountToolbarButtons: WidgetHelperButton[] = [];

    /** True while the „Abrechnungskonto ändern“ wizard dialog is open (ONE-WAY bound, see the template). */
    dialogBillingAccountChangeWizard: boolean = false;

    /** The account the wizard runs for — assigned once per open (account mode). */
    billingAccountChangeAccountId: number | null = null;

    /** The mounted wizard, asked by {@link requestBillingAccountChangeWizardClose}. */
    @ViewChild(BmCustomerBillingAccountChangeWizardComponent)
    billingAccountChangeWizard: BmCustomerBillingAccountChangeWizardComponent;

    /**
     * Round 11 (E-4): the import context the hosting `mvs-object` was given, forwarded by the
     * bm-customer-account-object wrapper. Its `bm.CustomerBillingAccount` entry may carry a SECTION
     * of this detail as `linkedAttribute` (the entry's third constructor parameter) - „n SEPA-Meldungen"
     * on an overview card asks for `'messages'`. ObjectBaseModeComponent has no such input, so it is
     * declared here.
     */
    @Input() importObjectContext: DtoImportObjectContext | null = null;

    /**
     * The section of the tab „Bankverbindungen & IBANs" the context asked for - handed to the tab
     * component, which scrolls it into view. Null when nothing was asked.
     */
    requestedBankConnectionsSection: BmBillingAccountDetailSection | null = null;

    // ------------------------------------------------------------------
    // Tab „Verlauf" (projects/bm-account-history): the shared history component in inline mode.
    // ------------------------------------------------------------------

    /** The signed completeness notice above the timeline (K-5). */
    readonly historyNotice: string = HISTORY_NOTICE;

    /** Empty state of the timeline. */
    readonly historyEmptyMessage: string = HISTORY_EMPTY_MESSAGE;

    /** German names of the scope types - the timeline's chips and the hint on types without read access. */
    readonly historyObjectTypeLabels: { [objectAlias: string]: string } = HISTORY_OBJECT_TYPE_LABELS;

    /** The open-ended date attributes of the scope - shown as „unbefristet“ like the tab „Bankverbindungen & IBANs“. */
    readonly historyOpenEndAttributes: { [objectAlias: string]: string[] } = HISTORY_OPEN_END_ATTRIBUTES;

    // ------------------------------------------------------------------
    // TM-1000001932: saldoneutrale "Vorausbuchung angenommener
    // Stornos/Wiederinkraftsetzungen" der aktiven Mahnung dieses Kontos.
    // Wird SEPARAT (nicht im rohen Saldo) als Finanzkennzahl ausgewiesen.
    // ------------------------------------------------------------------

    /** Aktive Vorausbuchung der aktiven Mahnung (null, wenn keine vorliegt). */
    anticipatedStornoBooking: DunningAnticipatedStornoBookingDto | null = null;

    /** True während des Ladens der Vorausbuchung. */
    anticipatedStornoLoading: boolean = false;

    /** True, wenn das Laden der Vorausbuchung fehlgeschlagen ist. */
    anticipatedStornoError: boolean = false;

    /** Mahnungs-Id, für die zuletzt geladen wurde (verhindert Doppel-Loads). */
    private anticipatedStornoLoadedDunningId: number | null = null;

    private anticipatedStornoSubscription: Subscription | null = null;

    constructor(
        protected override route: ActivatedRoute,
        protected override customerBillingAccountService: CustomerBillingAccountService,
        protected override objectService: MvsObjectService,
        // TM-1000001932: BD-Tracker-Read-Service (providedIn:'root').
        private readonly anticipatedStornoBookingService: BdAnticipatedStornoBookingService,
        // Opens a clicked contract in the left object bar. Injected HERE and not into the base
        // component on purpose: the side view shares that base and must stay free of navigation.
        private readonly navigationService: MvsObjectNavigationService
    ) {
        super(route, customerBillingAccountService, objectService);
    }

    /**
     * On Object Change. When object is retrieved
     */
    ngOnInit() {
        super.ngOnInit();
        this.refreshAccountHeaderActions();
        this.applyRequestedSection();
    }

    /**
     * The account of this view can be swapped in place (the object component is reused, e.g. from a
     * worklist), so an open wizard is closed with it — it must never keep running on the previously shown
     * account.
     */
    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        if (changes['objectIdentifier']) {
            this.dialogBillingAccountChangeWizard = false;
            this.refreshAccountHeaderActions();
        }

        if (changes['importObjectContext'] || changes['objectIdentifier']) {
            this.applyRequestedSection();
        }
    }

    /**
     * E-4: honour a section deep link of the import context - switch to the tab „Bankverbindungen &
     * IBANs" and tell it which section to scroll to. Only a context entry that names THIS account
     * counts; a request for another account (a stale context after an in-place swap) is ignored.
     */
    applyRequestedSection(): void {
        const section: BmBillingAccountDetailSection | null = BmCustomerAccountObjectFullComponent.requestedSection(
            this.importObjectContext, this.objectIdentifier?.objectId);
        this.requestedBankConnectionsSection = section;
        if (section === 'messages') {
            this.activeTabIndex = 2;
        }
    }

    /**
     * The section the context asks for, or null. Pure: the `bm.CustomerBillingAccount` entry's
     * `linkedAttribute` must be a known section AND the entry's id must be this account (ids compared
     * as numbers - backend Longs may arrive as strings). A context that cannot be read is „nothing asked".
     */
    static requestedSection(context: DtoImportObjectContext | null | undefined,
                            accountId: number | null | undefined): BmBillingAccountDetailSection | null {
        let entry: DtoImportObjectContextEntry | null;
        try {
            entry = context?.getEntry(BM_CUSTOMER_BILLING_ACCOUNT_OBJECT_TYPE_ALIAS) ?? null;
        } catch (error) {
            return null;
        }
        if (!entry || entry.linkedAttribute !== 'messages') {
            return null;
        }
        if (entry.id != null && accountId != null && Number(entry.id) !== Number(accountId)) {
            return null;
        }
        return 'messages';
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.anticipatedStornoSubscription?.unsubscribe();
    }

    /**
     * Rebuild the header action bar for the currently shown account.
     */
    private refreshAccountHeaderActions(): void {
        this.buildAccountToolbarButtons();
    }

    /**
     * AI:
     * Status: "in progress"
     * Type: Toolbar
     * SubType: Runtime
     * Reason: Header action bar of a bm.CustomerBillingAccount object view — the balance refresh plus
     *         the ONE launcher of the „Abrechnungskonto ändern“ wizard of this account.
     *
     * The wizard action is deliberately ALWAYS enabled, also for a closed account. Whether the
     * run may start at all is decided by the backend (paymentChangeContext -> wizardStartAllowed /
     * wizardStartBlockedReason) and stated by the wizard host in its blocked state, which names the
     * concrete reason in German. Disabling the button here would either duplicate that rule in the
     * frontend or — worse — hide it: a disabled action button must never be silent, and the header
     * has no room to spell the server's reason out. So the button opens, and the wizard explains.
     */
    private buildAccountToolbarButtons(): void {

        const refreshBalanceButton = WidgetFactory.createToolbarButton(
            LABEL_REFRESH_BALANCE, ICON_REFRESH_BALANCE, true,
            ACTION_REFRESH_BALANCE, TOOLTIP_REFRESH_BALANCE
        );

        // ONE entry for Zahlweise, Fälligkeitstag and Zahlungsfrequenz — the wizard explains every refusal
        const billingAccountChangeButton = WidgetFactory.createToolbarButton(
            LABEL_BILLING_ACCOUNT_CHANGE, ICON_BILLING_ACCOUNT_CHANGE, true,
            ACTION_OPEN_BILLING_ACCOUNT_CHANGE_WIZARD, TOOLTIP_BILLING_ACCOUNT_CHANGE
        );

        this.accountToolbarButtons = WidgetFactory.createToolbarButtons(
            refreshBalanceButton,
            billingAccountChangeButton
        );
    }

    /**
     * Single dispatch of the header action bar, keyed by the action id of the clicked button.
     */
    handleAccountToolbarClick(button: WidgetHelperButton): void {

        if (button.action === ACTION_REFRESH_BALANCE) {
            this.onRefreshBalance();
            return;
        }

        if (button.action === ACTION_OPEN_BILLING_ACCOUNT_CHANGE_WIZARD) {
            this.openBillingAccountChangeWizard();
        }
    }

    /** Open the „Abrechnungskonto ändern“ wizard in ACCOUNT mode for the account shown here. */
    openBillingAccountChangeWizard(): void {
        this.billingAccountChangeAccountId = this.objectIdentifier?.objectId ?? null;
        this.dialogBillingAccountChangeWizard = true;
    }

    /**
     * Only the balance refresh has a pending state of its own — it is the sole header action that
     * performs its write in place instead of handing over to a dialog.
     */
    isAccountToolbarButtonBusy(button: WidgetHelperButton): boolean {
        return button.action === ACTION_REFRESH_BALANCE && this.loading;
    }

    /**
     * The tooltip of a header action. Since the bar renders ICON-ONLY buttons, the tooltip is the
     * only visible carrier of what an action does — so a button without one falls back to its label
     * rather than hovering silent.
     */
    getAccountToolbarTooltip(button: WidgetHelperButton): string {
        return button?.tooltip || button?.label || '';
    }

    /** The wizard's own exit („Schließen“ in the closing step / a host state, or a confirmed X): hide the dialog. */
    closeBillingAccountChangeWizard(): void {
        this.dialogBillingAccountChangeWizard = false;
    }

    /** X / Escape are handed to the mounted run's close guard; no run mounted -> close directly. */
    requestBillingAccountChangeWizardClose(): void {

        if (!this.billingAccountChangeWizard) {
            this.closeBillingAccountChangeWizard();
            return;
        }

        this.billingAccountChangeWizard.requestClose();
    }

    /**
     * Bound to the dialog's (onHide), which fires on EVERY real close: only the open flag is lowered. The account is
     * NOT reloaded here (L-7, design §9.3): the wizard announces every write as `bm.CustomerBillingAccount` on the
     * base object bus, and the base view already re-reads on exactly that event
     * (`bm-customer-account-object-base.component.ts` `subscribeToObjectChanges`) — one mechanism, no double read.
     * No toast — the wizard's closing step is the feedback of the run.
     */
    handleBillingAccountChangeWizardHidden(): void {
        this.dialogBillingAccountChangeWizard = false;
    }

    /**
     * The bank-change wizard opened from the tab „Bankverbindungen & IBANs" ended (round 11, WP-B):
     * reload the account, because the run may have written a new bank slice - the header's bank
     * badge, IBAN and „gültig ab" are derived from that load. The tab reloads itself.
     */
    handleBankChangeWizardClosed(): void {
        this.getCustomerBillingAccount();
    }

    /**
     * Data-driven hook (TM-1000001932): the base calls it once the account read has completed (the
     * subscribe of getCustomerBillingAccount()) - never from a template interpolation, which used to
     * fire an HTTP fetch on every change-detection tick.
     *
     * Rebuilds the header's payment frequency tags from the loaded account: one tag per frequency of the
     * assignments that are active today AND of those that start later, the latter marked „ab {Datum}"
     * when the frequency occurs on not-yet-started assignments only ({@link recomputeContractFrequencyTags}).
     *
     * @ticket TM-1000002026
     */
    protected override onCustomerBillingAccountLoaded(): void {
        // TM-1000002026: active AND upcoming frequencies (was: active today only)
        this.recomputeContractFrequencyTags();
    }

    /**
     * The base rebuilds the visible rows (filter + order); this view additionally derives the display
     * rows of its table from them - on the same two triggers (load, switch), never per tick.
     *
     * @ticket TM-1000002026
     */
    override recomputeVisibleContractPaymentAccounts(): void {
        super.recomputeVisibleContractPaymentAccounts();
        this.contractLinkRows = bmBuildContractLinkRows(this.visibleContractPaymentAccounts);
    }

    /**
     * This view renders the open applications below the contract table of the Verträge tab, so it has
     * the base read them with every account (re)load (the side view does not).
     */
    protected override loadsOpenCwCaseLines(): boolean {
        return true;
    }

    /**
     * The number on the Verträge tab: it counts what the tab SHOWS - the contract rows the switch
     * „Abgelaufene Verträge anzeigen" lets through plus the open applications below them (which the
     * switch does not touch). A primitive, so the getter cannot churn change detection.
     */
    get contractsTabCount(): number {
        return (this.visibleContractPaymentAccounts?.length ?? 0) + (this.openCwCaseLines?.length ?? 0);
    }

    /**
     * Die laufende Mahnung wird getrennt vom Konto aus bd.Dunning gelesen (Status aktiv/ausgesetzt);
     * sobald sie vorliegt, stößt dieser Hook das Laden der Storno-Vorausbuchung an.
     */
    protected override onRunningDunningLoaded(): void {
        this.loadAnticipatedStornoBooking();
    }

    /**
     * Rebuilds {@link contractFrequencyTags} from the loaded contract payment accounts — ALL of them,
     * independent of the "Abgelaufene Verträge anzeigen" toggle, because the header states what this
     * account collects today and from when it will collect differently, not what the table shows.
     *
     * @ticket TM-1000002026
     */
    recomputeContractFrequencyTags(today: Date = new Date()): void {
        const entries = (this.customerBillingAccountDto?.contractPaymentAccounts ?? []).map(cpa => ({
            frequency: cpa?.frequency,
            linkStartDate: cpa?.startDate,
            linkEndDate: cpa?.endDate,
            contractEndDate: cpa?.contractDto?.endDate
        }));
        this.contractFrequencyTags = collectBmCurrentAndUpcomingPaymentFrequencies(entries, today)
            .map(occurrence => ({
                frequency: occurrence.frequency,
                label: bmPaymentFrequencyOccurrenceLabel(occurrence),
                tooltip: occurrence.activeToday
                    ? TOOLTIP_FREQUENCY_ACTIVE
                    : TOOLTIP_FREQUENCY_UPCOMING_PREFIX + bmCalendarDayNumberToDe(occurrence.upcomingFromDay)
            }));
    }

    /**
     * The header text of the payment day, e.g. "Einzug zum 1." or, for a Selbstzahler (never collected
     * from), "Fällig zum 15.". The day is a property of the account's billing run type
     * (bm.BillingRunType.paymentDayOfMonth), not of the account itself. Null when the billing run type
     * or its payment day is unknown — the header then shows nothing rather than a guess.
     */
    getPaymentDayHeaderLabel(): string | null {
        const day = this.customerBillingAccountDto?.billingRunTypeDto?.paymentDayOfMonth;
        const shortLabel = day == null ? null : BM_PAYMENT_DAY_OF_MONTH_SHORT_LABELS[day];
        if (!shortLabel) {
            return null;
        }
        const directDebit = isBmDirectDebitPaymentMethod(this.customerBillingAccountDto?.paymentMethod);
        const prefix = directDebit ? 'Einzug' : 'Fällig';
        return prefix + ' zum ' + shortLabel;
    }

    /**
     * TM-1000001932: Lädt die aktive Vorausbuchung der aktiven Mahnung dieses
     * Kontos (Betrag + Kommentar + Pro-Vertrag-Zusammensetzung) aus dem
     * BD-Tracker. Defensiv: ohne aktive Mahnung wird nichts geladen.
     * Wird aus dem Datenfluss (onCustomerBillingAccountLoaded) angestoßen.
     */
    private loadAnticipatedStornoBooking(): void {
        const dunningId = this.runningDunning?.dunningId;
        if (!dunningId) {
            this.anticipatedStornoBooking = null;
            this.anticipatedStornoLoadedDunningId = null;
            return;
        }
        // Bereits für diese Mahnung geladen → kein erneuter Call (Template-getrieben).
        if (this.anticipatedStornoLoadedDunningId === dunningId
            || this.anticipatedStornoLoading) {
            return;
        }

        this.anticipatedStornoLoadedDunningId = dunningId;
        this.anticipatedStornoLoading = true;
        this.anticipatedStornoError = false;
        this.anticipatedStornoSubscription?.unsubscribe();
        this.anticipatedStornoSubscription = this.anticipatedStornoBookingService
            .listActiveByDunning(dunningId)
            .subscribe({
                next: (response: DtoList<DunningAnticipatedStornoBookingDto>) => {
                    this.anticipatedStornoBooking = response.entries?.length ? response.entries[0] : null;
                    this.anticipatedStornoLoading = false;
                },
                error: () => {
                    this.anticipatedStornoBooking = null;
                    this.anticipatedStornoLoading = false;
                    this.anticipatedStornoError = true;
                }
            });
    }

    /**
     * TM-1000001932: Roher Gesamtsaldo des Kontos (Ledger-Konvention:
     * Forderung = negativ). Quelle ist UNVERÄNDERT der persistierte Saldo.
     */
    get rawBalance(): number {
        return this.customerBillingAccountDto?.customerBillingAccountBalanceDto?.balance ?? 0;
    }

    /**
     * TM-1000001932: Vorzeichenbehafteter Betrag der aktiven Vorausbuchung in
     * Ledger-Konvention (positiv = Gutschrift). 0, wenn keine vorliegt.
     */
    get anticipatedStornoAmount(): number {
        return this.anticipatedStornoBooking?.netAmount ?? 0;
    }

    /** TM-1000001932: true, wenn eine aktive Vorausbuchung vorliegt. */
    get hasAnticipatedStornoBooking(): boolean {
        return this.anticipatedStornoBooking != null && this.anticipatedStornoAmount !== 0;
    }

    /**
     * TM-1000001932: Effektiver Saldo NACH Vorausbuchung (nur Anzeige-Ausblick;
     * verändert NICHT den rohen Saldo). Ledger-Konvention.
     */
    get effectiveBalanceAfterAnticipated(): number {
        return this.rawBalance + this.anticipatedStornoAmount;
    }

    /** Währung der Vorausbuchung (Fallback: Konto-Währung). */
    get anticipatedStornoCurrency(): string {
        return this.anticipatedStornoBooking?.currency
            || this.customerBillingAccountDto?.currency
            || 'EUR';
    }

    /**
     * Toggle expand/collapse of ONE assignment row. Lazily creates its widgets on the first expand.
     *
     * TM-1000002026: keyed by the assignment id (`cpa.id`), no longer by the contract id - the table
     * lists one row per assignment slice, and two slices of the same contract opened and closed
     * together. The widgets still show the CONTRACT's prices and planned payments.
     */
    toggleContractRow(cpa: ContractPaymentAccountDto): void {
        const assignmentId = cpa?.id;
        if (assignmentId == null) {
            return;
        }
        this.expandedContractRows[assignmentId] = !this.expandedContractRows[assignmentId];

        // Lazily create widgets on first expand
        if (this.expandedContractRows[assignmentId] && !this.contractPriceWidgets[assignmentId]
            && cpa.contractDtoId != null) {
            this.createContractSubWidgets(assignmentId, cpa.contractDtoId);
        }
    }

    /**
     * Whether the row of this assignment is expanded.
     *
     * TM-1000002026: per assignment id, see {@link toggleContractRow}.
     */
    isContractRowExpanded(cpa: ContractPaymentAccountDto): boolean {
        return cpa?.id != null && !!this.expandedContractRows[cpa.id];
    }

    /**
     * Opens the clicked contract as a cm.Contract object in the LEFT object bar.
     *
     * Navigation goes through {@link MvsObjectNavigationService}, not through a route and not through
     * a dialog: the account stays where it is and the contract opens beside it, which is what the
     * rest of the platform does for a drill-down out of a table row.
     *
     * The only trigger is the VSNR link of a contract row — not the row itself, so the clickable
     * area is exactly what the underline advertises. The optional event exists for that click; the
     * keyboard path calls this without one.
     */
    openContract(cpa: ContractPaymentAccountDto, event?: MouseEvent): void {
        const contractId = cpa?.contractDtoId;
        if (contractId == null) {
            return;
        }

        event?.stopPropagation();

        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            'cm.Contract', Number(contractId), 'object',
            null, null, null, MvsObjectNavigationActionEnum.any
        );
        this.navigationService.navigateTo(entry, 'left');
    }

    /**
     * Opens the Vorgang of an open application as a cw.CwCase object in the LEFT object bar - the same
     * drill-down as {@link openContract}, and like there the Vorgangsnummer link is the only trigger.
     * The object type is a string on purpose: bm must not import from cw (BOUND-002/003).
     */
    openCwCase(line: BmOpenCwCaseLine, event?: MouseEvent): void {
        const caseId = line?.caseId;
        if (caseId == null) {
            return;
        }

        event?.stopPropagation();

        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            BM_CW_CASE_OBJECT_TYPE, Number(caseId), 'object',
            null, null, null, MvsObjectNavigationActionEnum.any
        );
        this.navigationService.navigateTo(entry, 'left');
    }

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: Runtime
     * Reason: Sub-tables of an expanded assignment row of this account — the cm.ContractPrice slices and
     *         the bm.ContractPaymentPlanned entries of that row's contract.
     *
     * TM-1000002026: cached per ASSIGNMENT id (one pair of widget instances per expanded row), filtered
     * by the CONTRACT id.
     */
    createContractSubWidgets(assignmentId: number, contractId: number): void {
        // ContractPrice sub-table: shows all prices for this contract
        this.contractPriceWidgets[assignmentId] = WidgetFactory.createWidgetTableEntityBasicQl(
            'bm.account.contract.prices',
            'Beiträge',
            'cm.ContractPrice',
            [FilterCriteria.create('e.contractId', FilterCriteria.cOperatorEqual, contractId)],
            [new Sorting('e.startDate', false)],
            true
        );

        // ContractPaymentPlanned sub-table: shows planned payments for this contract
        this.contractPaymentPlannedWidgets[assignmentId] = WidgetFactory.createWidgetTableEntityBasicQl(
            'bm.account.contract.payments',
            'Geplante Zahlungen',
            'bm.ContractPaymentPlanned',
            [FilterCriteria.create('e.contractId', FilterCriteria.cOperatorEqual, contractId)],
            [new Sorting('e.dueDate', false)],
            true
        );
    }

    // TM-1000002026: getContractStatusSeverity removed - [severity] is inert app-wide; the status tags
    // carry the base status enum's own colours as a styleClass (bm-contract-status-tag-style, row model)

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: Runtime
     * Reason: Detail dialog listing all bm.ContractPaymentAccount rows of this billing account, i.e.
     *         every contract that is settled through it together with its price.
     */
    openContractPriceDetail(): void {
        this.showContractPriceDetail = true;
        if (!this.contractPriceDetailWidget) {
            this.contractPriceDetailWidget = WidgetFactory.createWidgetTableEntityBasicQl(
                'bm.account.contract.price.detail',
                'Verträge & Beiträge',
                'bm.ContractPaymentAccount',
                [FilterCriteria.create('e.customerBillingAccountId', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId)],
                [new Sorting('e.startDate', false)],
                true
            );
        }
    }

}
